///
/// EarthBrowser
///
/// Created by Matt Giger on 12/31/2023
/// Copyright (c) 2023 Geodata Labs
///

import { type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { ImageLoader } from "@/earthbrowser/utils/loaders/ImageLoader"
import { underMemoryPressure } from "@/earthbrowser/utils/MemoryBudget"
import { datasetUrl } from "@/utils/datasetUrl"
import { type RenderState } from "@stovokor/engine/RenderState"
import { clamp } from "@stovokor/math/math"
import { time, timedelta, timerange } from "@stovokor/math/time"
import { ComponentType, InternalFormat, PixelFormat } from "@stovokor/shader/GLConstants"
import { Texture } from "@stovokor/shader/Texture"
import { cCacheAgeOneWeek, type FetchPriority } from "@/earthbrowser/utils/loaders/FetchLoader"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type EarthBrowserContext } from "../EarthBrowserContext"

const cTimeUpdateInterval = 1000		// one second in milliseconds
// Catch-up crossfade duration (wall clock) used when the current frame
// finishes loading after a fallback frame was displayed in its place
const cHandoffFadeMs = 250

// ==================== Dev instrumentation ====================
// Lightweight counters surfaced via window.__ebDebug.getTextureSeriesStats()
// (see utils/debugHooks.ts) to diagnose scrub/animation stalls: how long
// atlas loads take, how many are in flight, and how far the displayed frame
// lags the frame the clock wants.

interface TextureLoadEvent {
	url: string
	ms: number
	ok: boolean
	priority: FetchPriority
}

export const textureLoadStats = {
	started: 0,
	completed: 0,
	failed: 0,
	inFlight: 0,
	totalMs: 0,
	maxMs: 0,
	recent: [] as TextureLoadEvent[],
}

function recordLoadEnd(url: string, startedAt: number, ok: boolean, priority: FetchPriority): void {
	const ms = performance.now() - startedAt
	textureLoadStats.inFlight--
	if(ok) {
		textureLoadStats.completed++
	} else {
		textureLoadStats.failed++
	}
	textureLoadStats.totalMs += ms
	textureLoadStats.maxMs = Math.max(textureLoadStats.maxMs, ms)
	textureLoadStats.recent.push({ url: url.split("/").pop() ?? url, ms: Math.round(ms), ok, priority })
	if(textureLoadStats.recent.length > 40) {
		textureLoadStats.recent.shift()
	}
}

export class TextureSeries<FrameType extends TextureFrame> {

	// Dev instrumentation: registry of live series for debugSnapshots()
	private static registry = new Set<WeakRef<TextureSeries<TextureFrame>>>()
	public debugLabel = ""
	private switchCount = 0
	private switchedAt = 0
	private waitingForCurrent = false
	private purgeCount = 0
	private timeToCurrentMs: number[] = []
	public fadeFactor: number = 0
	public blendFactor: number = 0
	public texture0: Texture
	public texture1: Texture

	/**
	 * Keep texture0/texture1 packed into one RG texture (r = texture0,
	 * g = texture1) for shaders that are short on texture units: the earth
	 * tile overlays sample `pairTexture` instead of the two frames. Opt-in
	 * because the pack costs a fullscreen pass at frame resolution whenever
	 * the pair changes, and consumers with units to spare don't need it
	 */
	public packPairs: boolean = false
	private pair?: Texture
	private packed0?: Texture
	private packed1?: Texture
	private packedAvailable0 = false
	private packedAvailable1 = false

	public currentFrame?: FrameType
	public nextFrame?: FrameType
	protected range = new timerange(time.longAgo, time.longAgo)
	protected beginFadeDelta = timedelta.hours(0)
	protected endOffset: timedelta = timedelta.hours(1)
	protected frames: FrameType[] = []
	protected preloadCount: number = 5
	protected backPreloadCount: number = 0
	protected purgeUnused: boolean = false
	protected updateInterval: number = cTimeUpdateInterval
	protected lastUpdateTime: number = 0

	// The frame whose texture is currently displayed. Usually currentFrame,
	// but while that frame is still loading this falls back to the nearest
	// loaded frame so scrubbing shows data instead of blanking
	private displayFrame?: FrameType

	// The current frame we were waiting on last render while a fallback was
	// displayed in its place (undefined when displaying the current frame)
	private waitingDisplayFor?: FrameType

	// Catch-up crossfade state: when the awaited current frame becomes
	// available, fade from the fallback frame to it over cHandoffFadeMs
	// instead of popping. Only ever starts on recovery from a fallback, so
	// it cannot fire on an ordinary frame advance
	private handoffFrom?: FrameType
	private handoffTo?: FrameType
	private handoffStartMs = 0

	private context: EarthBrowserContext

	constructor(context: EarthBrowserContext) {
		this.context = context
		this.texture0 = context.blankTexture
		this.texture1 = context.blankTexture
		TextureSeries.registry.add(new WeakRef(this))
	}

	/** Dev instrumentation: snapshot every live series (dead refs are pruned) */
	public static debugSnapshots(): Array<Record<string, unknown>> {
		const out: Array<Record<string, unknown>> = []
		for(const ref of TextureSeries.registry) {
			const series = ref.deref()
			if(series) {
				out.push(series.debugSnapshot())
			} else {
				TextureSeries.registry.delete(ref)
			}
		}
		return out
	}

	/** Dev instrumentation: current state of this series */
	public debugSnapshot(): Record<string, unknown> {
		const current = this.currentFrame
		const currentIndex = current ? this.frames.indexOf(current) : -1
		const displayIndex = this.displayFrame ? this.frames.indexOf(this.displayFrame) : -1
		return {
			label: this.debugLabel || this.constructor.name,
			frames: this.frames.length,
			loaded: this.frames.filter(f => f.available).length,
			loading: this.frames.filter(f => f.isLoading).length,
			currentIndex,
			currentAvailable: current?.available ?? false,
			displayIndex,
			displayLag: currentIndex >= 0 && displayIndex >= 0 ? currentIndex - displayIndex : 0,
			// catch-up crossfade from a fallback frame still in progress
			crossfading: this.handoffTo !== undefined,
			switches: this.switchCount,
			purges: this.purgeCount,
			// how long the clock has been pointing at a frame we can't show yet
			waitingForCurrentMs: this.waitingForCurrent ? Math.round(performance.now() - this.switchedAt) : 0,
			// recent history of how long each frame switch took to display correctly
			recentTimeToCurrentMs: this.timeToCurrentMs.slice(),
		}
	}

	// Dev instrumentation: called whenever currentFrame changes
	private noteFrameSwitch(): void {
		this.switchCount++
		this.switchedAt = performance.now()
		this.waitingForCurrent = !(this.currentFrame?.available ?? false)
	}

	/** Time span covered by the current frame list (undefined when empty) */
	public get timeRange(): timerange | undefined {
		return this.frames.length > 0 ? this.range : undefined
	}

	/**
	 * The clock points at a frame whose texture isn't on the GPU yet, so
	 * the screen shows a fallback frame (or nothing). Null once the wanted
	 * frame is displayed. `atMs` is the wanted frame's time; `since` the
	 * moment the clock switched to it
	 */
	public loadState(): LayerLoadState | null {
		const current = this.currentFrame
		if(!current || current.available) {
			return null
		}
		return {
			phase: "frames",
			atMs: current.range.start.date.getTime(),
			since: this.switchedAt,
		}
	}

	/**
	 * Start time (ms) of the frame actually on screen while it differs
	 * from the frame the clock wants (a fallback during loading), else
	 * undefined. Lets the timeline label the picture honestly
	 */
	public displayedFrameTimeMs(): number | undefined {
		const shown = this.displayFrame
		if(!shown || !shown.available || shown === this.currentFrame) {
			return undefined
		}
		return shown.range.start.date.getTime()
	}

	/** Start time (ms) of every frame, ascending. Feeds the timeline's frame stepping */
	public frameTimesMs(): number[] {
		return this.frames.map(f => f.range.start.date.getTime())
	}

	/**
	 * Time spans whose textures are resident on the GPU, merging runs of
	 * consecutive loaded frames. Feeds the timeline's "buffered" indicator
	 * so users can see why scrubbing into unfetched frames lags.
	 */
	public loadedRangesMs(): Array<{ startMs: number; endMs: number }> {
		const out: Array<{ startMs: number; endMs: number }> = []
		const count = this.frames.length
		for(let i = 0; i < count; ++i) {
			const frame = this.frames[i]
			if(!frame.available) {
				continue
			}
			const startMs = frame.range.start.date.getTime()
			// a loaded frame covers up to the next frame's start (or its own end)
			const next = this.frames[i + 1]
			const endMs = next ? next.range.start.date.getTime() : frame.range.end.adding(this.endOffset).date.getTime()
			const last = out[out.length - 1]
			if(last && last.endMs >= startMs) {
				last.endMs = Math.max(last.endMs, endMs)
			} else {
				out.push({ startMs, endMs })
			}
		}
		return out
	}

	public clear(): void {
		this.frames.forEach(frame => frame.purge())
		this.frames = []
		this.currentFrame = undefined
		this.nextFrame = undefined
		this.displayFrame = undefined
		this.resetHandoff()
		this.range = new timerange(time.longAgo, time.longAgo)
		this.lastUpdateTime = 0

		this.fadeFactor = 0
		this.blendFactor = 0
		this.texture0 = this.context.blankTexture
		this.texture1 = this.context.blankTexture
		this.releasePair()
	}

	/**
	 * Free every frame's GPU texture but keep the frame list, so a hidden
	 * layer drops its texture memory yet can stream back in when re-shown.
	 * Resetting currentFrame/lastUpdateTime forces the next updateState to
	 * re-select the current frame and issue fresh loads.
	 */
	public purgeAll(): void {
		this.frames.forEach(frame => frame.purge())
		this.currentFrame = undefined
		this.nextFrame = undefined
		this.displayFrame = undefined
		this.resetHandoff()
		this.lastUpdateTime = 0

		this.fadeFactor = 0
		this.blendFactor = 0
		this.texture0 = this.context.blankTexture
		this.texture1 = this.context.blankTexture
		this.releasePair()
	}

	/**
	 * The packed frame pair (see packPairs); a blank texture until the
	 * first pack, which reads as "no data" like the blank frames do
	 */
	public get pairTexture(): Texture {
		return this.pair ?? this.context.blankTexture
	}

	/** Drop the packed pair's GPU memory; the next updateState rebuilds it */
	private releasePair(): void {
		this.pair?.destroy()
		this.pair = undefined
		this.packed0 = undefined
		this.packed1 = undefined
	}

	/**
	 * Repack texture0/texture1 when either changed since the last pack.
	 * A frame's texture object is created when its load starts and
	 * replaced on purge, so identity catches frame switches; the available
	 * flag catches the one in-place change, a displayed frame's texture
	 * finishing its upload. Mipmaps follow the frames: a series whose
	 * frames are minified at globe scale (clouds) needs them on the pack
	 */
	private updatePair(): void {
		if(!this.packPairs) {
			return
		}
		if(this.texture0 === this.packed0 && this.texture1 === this.packed1
			&& this.texture0.available === this.packedAvailable0 && this.texture1.available === this.packedAvailable1) {
			return
		}
		this.pair ??= this.context.framePairs.createTarget()
		const minFilter = this.texture0.minFilter
		const mipmaps = minFilter === WebGL2RenderingContext.LINEAR_MIPMAP_LINEAR
			|| minFilter === WebGL2RenderingContext.LINEAR_MIPMAP_NEAREST
		if(this.context.framePairs.pack(this.texture0, this.texture1, this.pair, mipmaps)) {
			this.packed0 = this.texture0
			this.packed1 = this.texture1
			this.packedAvailable0 = this.texture0.available
			this.packedAvailable1 = this.texture1.available
		}
	}

	private resetHandoff(): void {
		this.waitingDisplayFor = undefined
		this.handoffFrom = undefined
		this.handoffTo = undefined
	}

	// Set new frames while preserving existing and overlapping frames (determined by URL uniqueness)
	public setFrames(frames: FrameType[]): void {
		this.currentFrame = undefined
		this.nextFrame = undefined
		this.lastUpdateTime = 0

		// Old frames must be compared against the INCOMING list: comparing
		// against this.frames (as this used to) never purged anything, so
		// GPU textures and frame objects leaked on every refresh
		const purgedFrames = this.frames.filter((frame) => { return !TextureSeries.hasFrame(frames, frame.url) })
		purgedFrames.forEach(frame => frame.purge())

		const newFrames = frames.filter((frame) => { return !TextureSeries.hasFrame(this.frames, frame.url) })
		const overlapFrames = this.frames.filter((frame) => { return TextureSeries.hasFrame(frames, frame.url) })

		// Keep what's on screen (and a catch-up crossfade in flight) when
		// those frames survive the refresh. A live-mode refresh that only
		// appends the newest frame must not forget the frame being shown:
		// that frame is the loading fallback for the new one, and resetting
		// it here let the purge below evict the only loaded texture, blanking
		// the layer until the new frame downloaded
		const kept = new Set<FrameType>(overlapFrames)
		const survives = (frame: FrameType | undefined): boolean => frame !== undefined && kept.has(frame)
		if(!survives(this.displayFrame)) {
			this.displayFrame = undefined
		}
		if(!survives(this.waitingDisplayFor)) {
			this.waitingDisplayFor = undefined
		}
		if(!survives(this.handoffFrom) || !survives(this.handoffTo)) {
			this.handoffFrom = undefined
			this.handoffTo = undefined
		}

		this.frames = overlapFrames.concat(newFrames).sort((a, b) => { return a.range.start.julian - b.range.start.julian })
		const first = this.frames[0]
		const last = this.frames[this.frames.length-1]
		if(first && last) {
			this.range.start = first.range.start
			this.range.end = last.range.end.adding(this.endOffset)
			last.endOffset = this.endOffset
		} else {
			this.range = new timerange()
		}
	}

	private static hasFrame(array: TextureFrame[], url: string): boolean {
		for(const frame of array) {
			if(frame.url == url) {
				return true
			}
		}
		return false
	}

	public updateState(_state: RenderState): void {

		const sceneTime = this.context.clock.clockTime

		// Throttle the frame search / preload / purge bookkeeping, but always
		// run the display section below: textures that finish loading must
		// show up immediately, even when the clock is paused after a scrub
		let purgeDue = false
		if(Math.abs(sceneTime.date.getTime() - this.lastUpdateTime) >= this.updateInterval) {
			this.lastUpdateTime = sceneTime.date.getTime()
			this.updateFrames(sceneTime)
			purgeDue = true
		}

		// calculate the cloud fade factor
		this.fadeFactor = 1.0
		const startFade = sceneTime.adding(this.beginFadeDelta)
		if(sceneTime.greater(startFade)) {
			const endFade = sceneTime.adding(this.endOffset)
			if(sceneTime.less(endFade)) {
				const endRange = endFade.difference(sceneTime)
				const fadeRange = endFade.difference(startFade)
				this.fadeFactor = endRange.jdelta / fadeRange.jdelta
			} else {
				this.fadeFactor = 0.0
			}
		}

		// Dev instrumentation: record how long the switch to the current
		// frame took before it could actually be displayed
		if(this.waitingForCurrent && this.currentFrame?.available) {
			this.waitingForCurrent = false
			this.timeToCurrentMs.push(Math.round(performance.now() - this.switchedAt))
			if(this.timeToCurrentMs.length > 20) {
				this.timeToCurrentMs.shift()
			}
		}

		// Pick the frame to display: the current frame once its texture is
		// loaded, otherwise the nearest loaded frame so scrubbing never
		// blanks while atlases stream in
		const currentFrame = this.currentFrame
		let displayFrame = currentFrame
		if(currentFrame && !currentFrame.available) {
			const nearest = this.nearestAvailableFrame(currentFrame)
			if(nearest) {
				displayFrame = nearest
			} else if(this.displayFrame?.available) {
				// nothing else is loaded — keep showing the last displayed
				// frame rather than flickering off
				displayFrame = this.displayFrame
			}
		}

		// The current frame just became available after a fallback was shown
		// for it: start a short crossfade from the fallback so the display
		// catches up smoothly instead of popping
		if(currentFrame && displayFrame === currentFrame && this.waitingDisplayFor === currentFrame) {
			const from = this.displayFrame
			if(from && from !== currentFrame && from.available) {
				this.handoffFrom = from
				this.handoffTo = currentFrame
				this.handoffStartMs = performance.now()
			}
		}
		this.waitingDisplayFor = displayFrame !== currentFrame ? currentFrame : undefined
		this.displayFrame = displayFrame

		// Cancel the crossfade when it finishes, the clock moved on to a
		// different frame, or the fallback frame's texture was purged
		if(this.handoffTo) {
			const elapsed = performance.now() - this.handoffStartMs
			if(elapsed >= cHandoffFadeMs || this.handoffTo !== currentFrame || !this.handoffFrom?.available) {
				this.handoffFrom = undefined
				this.handoffTo = undefined
			}
		}

		this.selectDisplayTextures(sceneTime, currentFrame, displayFrame)

		// Purge only after the display frame is settled so the keep set
		// protects the frame actually on screen. Purging inside updateFrames
		// (as this used to) ran before the fallback was chosen: in live mode
		// the lookahead window is empty past the newest frame, so a refresh
		// that appended an unloaded frame evicted the loaded one being
		// displayed, and the layer blanked until the download finished
		if(purgeDue) {
			this.purgeStaleFrames()
		}
		this.updatePair()
	}

	/** Set texture0/texture1 and blendFactor for the frame being displayed */
	private selectDisplayTextures(sceneTime: time, currentFrame: FrameType | undefined, displayFrame: FrameType | undefined): void {
		if(this.handoffFrom && this.handoffTo) {
			// Catch-up crossfade: fade from the stale fallback frame to the
			// now-loaded current frame on wall-clock time
			this.texture0 = this.handoffFrom.texture
			this.texture1 = this.handoffTo.texture
			this.blendFactor = clamp((performance.now() - this.handoffStartMs) / cHandoffFadeMs, 0, 1)
			return
		}

		this.texture0 = displayFrame?.texture ?? this.context.blankTexture
		this.texture1 = this.context.blankTexture

		// Blend toward the next frame only when we're actually displaying the
		// current frame; a fallback frame is shown as-is
		this.blendFactor = 0
		if(currentFrame && displayFrame === currentFrame) {
			const nextFrame = this.nextFrame
			if(nextFrame) {
				const sceneDiff = sceneTime.difference(currentFrame.range.start)
				const frameDiff = nextFrame.range.start.difference(currentFrame.range.start)

				// Match TileSeries behavior: always compute blend factor when we have a next frame.
				// Guard against duplicate/zero-length frame spacing.
				if(frameDiff.jdelta > 0) {
					this.blendFactor = clamp(sceneDiff.jdelta / frameDiff.jdelta, 0, 1)
				}

				// If next texture isn't ready yet, keep texture1 equal to texture0 so we
				// don't fade to blank while still allowing blendFactor to vary.
				this.texture1 = nextFrame.texture?.available ? nextFrame.texture : this.texture0
			}
		}
	}

	// Frame selection, preloading and purging (throttled by updateInterval)
	private updateFrames(sceneTime: time): void {

		const count = this.frames.length
		if(count === 0) {
			this.currentFrame = undefined
			this.nextFrame = undefined
			return
		}

		// Display the latest frame at or before the clock: inside a frame's
		// window that's the frame itself; in the gap between two scans it
		// holds the earlier one (blending toward the next); past the last
		// frame it holds the newest, which is what live mode needs since
		// "now" is always a little after the last scan. Before the first
		// frame there is no data yet, so show nothing rather than a frame
		// from the future.
		const currentFrameIndex = this.frameIndexAt(sceneTime)

		if(currentFrameIndex < 0) {
			this.currentFrame = undefined
			this.nextFrame = undefined
			return
		}

		const { ahead: effectivePreload, behind: effectiveBackPreload } = this.effectiveWindow()

		const newCurrentFrame = this.frames[currentFrameIndex]
		if(newCurrentFrame !== this.currentFrame) {
			this.currentFrame = newCurrentFrame
			this.nextFrame = currentFrameIndex + 1 < count ? this.frames[currentFrameIndex + 1] : undefined
			// The frame being displayed right now outranks every speculative
			// load queued by any series sharing the connection
			this.currentFrame.load("high")
			this.noteFrameSwitch()

			// Preload a window around the current frame — a few behind for
			// backward scrubbing, preloadCount ahead (wrapping at the clock's
			// loop point to support looped playback). Low priority: these
			// must never queue ahead of another series' current frame
			for(const index of this.preloadWindow(currentFrameIndex, effectivePreload, effectiveBackPreload)) {
				const preloadFrame = this.frames[index]
				if(!preloadFrame.available) {
					preloadFrame.load("low")
				}
			}
		}
	}

	// Graceful degradation: over the soft memory budget, keep only the
	// next frame ahead and nothing behind. Playback still works (frames
	// stream on demand); only the speculative lookahead shrinks. The
	// purge uses the same effective counts, so shrinking also actively
	// frees frames loaded before pressure set in. preloadWindow's `ahead`
	// counts the current frame, so 2 is the smallest value that still
	// loads the next frame; 1 left nothing to blend toward and turned
	// playback into a slideshow under pressure
	private effectiveWindow(): { ahead: number; behind: number } {
		const pressured = underMemoryPressure()
		return {
			ahead: pressured ? Math.min(this.preloadCount, 2) : this.preloadCount,
			behind: pressured ? 0 : this.backPreloadCount,
		}
	}

	// Free frames outside the preload window (ahead and behind), keeping
	// the frame on screen (the loading fallback) and a crossfade source.
	// Runs after display selection; see updateState
	private purgeStaleFrames(): void {
		if(!this.purgeUnused || !this.currentFrame) {
			return
		}
		const { ahead, behind } = this.effectiveWindow()
		const keep = this.preloadWindow(this.frames.indexOf(this.currentFrame), ahead, behind)
		const count = this.frames.length
		for(let i = 0; i < count; ++i) {
			const frame = this.frames[i]
			if(keep.has(i) || frame === this.currentFrame || frame === this.nextFrame || frame === this.displayFrame || frame === this.handoffFrom) {
				continue
			}
			if(frame.available || frame.isLoading) {
				this.purgeCount++
			}
			frame.purge()
		}
	}

	/** Latest frame index whose start is at or before the given time (-1 if none) */
	private frameIndexAt(t: time): number {
		for(let index = this.frames.length - 1; index >= 0; --index) {
			if(this.frames[index].range.start.lessOrEqual(t)) {
				return index
			}
		}
		return -1
	}

	// Frame indexes to keep loaded around the current frame: `behind` frames
	// for backward scrubbing plus a lookahead window of `ahead` (including
	// the current frame). While the clock is animating, frames past the end
	// of its window are unreachable — the clock wraps to the window start —
	// so that part of the lookahead redirects to the frames at the loop
	// target instead. This lets looped playback replay without stalling on
	// texture loads. (Wrapping at the frame-array end, as this used to,
	// preloaded the oldest scans, which a looping clock never reaches.)
	private preloadWindow(index: number, ahead: number, behind: number): Set<number> {
		const count = this.frames.length
		const indexes = new Set<number>()

		for(let i = 1; i <= behind; ++i) {
			indexes.add((index - i + count) % count)
		}

		const clock = this.context.clock
		let endIndex = count - 1
		let wrapIndex = -1
		if(clock.clockState === "animating") {
			endIndex = this.frameIndexAt(clock.range.end)
			wrapIndex = Math.max(0, this.frameIndexAt(clock.range.start))
		}

		let overflow = 0
		for(let i = 1; i < ahead; ++i) {
			const idx = index + i
			if(wrapIndex >= 0 && idx > endIndex) {
				const wrapped = wrapIndex + overflow++
				if(wrapped < count) {
					indexes.add(wrapped)
				}
			} else if(idx < count) {
				indexes.add(idx)
			}
		}
		return indexes
	}

	// Nearest loaded frame to the given frame (searching outward both ways)
	private nearestAvailableFrame(around: FrameType): FrameType | undefined {
		const index = this.frames.indexOf(around)
		const count = this.frames.length
		for(let offset = 1; offset < count; ++offset) {
			const before = index - offset >= 0 ? this.frames[index - offset] : undefined
			if(before?.available) {
				return before
			}
			const after = index + offset < count ? this.frames[index + offset] : undefined
			if(after?.available) {
				return after
			}
			if(!before && !after) {
				break
			}
		}
		return undefined
	}
}

export class TextureFrame {
	public url: string
	public range: timerange
	public endOffset = timedelta.hours(0)
	public texture: Texture
	public metadata: Record<string, string | number> = {}
	public available: boolean = false
	protected loading: boolean = false
	protected textureLoader?: ImageLoader
	protected context: StovokorContext

	constructor(context: StovokorContext, url: string, startTime: time, duration: timedelta) {
		this.context = context
		this.url = url
		this.range = new timerange(startTime, startTime.adding(duration))
		this.texture = context.blankTexture
	}

	public inRange(time: time): boolean {
		return this.range.start.lessOrEqual(time) && this.range.end.adding(this.endOffset).greaterOrEqual(time)
	}

	public get isLoading(): boolean {
		return this.loading
	}

	public purge(): void {
		// Abort any in-flight load so it can't upload into the destroyed texture
		this.textureLoader?.abort()
		this.textureLoader = undefined
		this.available = false
		this.loading = false
		if(this.texture != this.context.blankTexture) {
			this.texture.destroy()
			this.texture = this.context.blankTexture
		}
	}

	public load(priority: FetchPriority = "auto"): void {
		if(this.loading || this.available) {
			return
		}
		this.loading = true
		if(this.texture == this.context.blankTexture) {
			this.createNewTexture()
		}
		const startedAt = performance.now()
		textureLoadStats.started++
		textureLoadStats.inFlight++
		this.textureLoader = new ImageLoader(datasetUrl(this.url), { lifetime: cCacheAgeOneWeek, priority })
		this.textureLoader.loadIntoTexture(this.texture)
			.then((success) => {
				this.available = success
				this.metadata = this.textureLoader?.metadata ?? {}
				this.loading = false
				recordLoadEnd(this.url, startedAt, success, priority)
			})
			.catch(e => {
				this.loading = false
				recordLoadEnd(this.url, startedAt, false, priority)
				console.error(`[TextureFrame] Load ERROR for URL: ${this.url}`, e)
			})
			.finally(() => { this.textureLoader = undefined })
	}

	protected createNewTexture(): void {
		this.texture = new Texture(this.context, 1, 1, InternalFormat.RGBA, PixelFormat.RGBA, ComponentType.UnsignedByte)
	}
}
